import { Controller, useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { ActivityMatrixQuestionProps } from '../../types';
import { Activity, ActivityNumberStepper, ActivityValue } from '../components/activity-matrix';
import { QuestionActivityMatrix } from '@/api/generated-catch-backend/model';

export const ActivityNumberStepperQuestion = ({ question }: ActivityMatrixQuestionProps) => {
  const { control, setValue, getValues } = useFormContext();

  // Cast to proper type to ensure we have the activities array
  const activityQuestion = question as QuestionActivityMatrix;

  // Default activities if none provided in question
  const defaultActivities: Activity[] = [
    {
      id: 'walking_briskly',
      name: 'Walking briskly',
      values: { daysPerWeek: 0, minutesPerDay: 0 },
    },
    {
      id: 'running',
      name: 'Running',
      values: { daysPerWeek: 0, minutesPerDay: 0 },
    },
    {
      id: 'cycling',
      name: 'Cycling',
      values: { daysPerWeek: 0, minutesPerDay: 0 },
    },
    {
      id: 'swimming',
      name: 'Swimming',
      values: { daysPerWeek: 0, minutesPerDay: 0 },
    },
  ];

  const convertToActivity = (option: string): Activity => ({
    id: option,
    name: option,
    values: { daysPerWeek: 0, minutesPerDay: 0 },
  });

  const activities =
    activityQuestion.activities && activityQuestion.activities.length > 0
      ? activityQuestion.activities.map(convertToActivity)
      : defaultActivities;

  useEffect(() => {
    const currentValue = getValues(activityQuestion.slug);
    const hasNoValue = !currentValue || Object.keys(currentValue).length === 0;

    if (hasNoValue) {
      const properDefaults: Record<string, ActivityValue> = {};
      activities.forEach((activity) => {
        properDefaults[activity.id] = { daysPerWeek: 0, minutesPerDay: 0 };
      });
      setValue(activityQuestion.slug, properDefaults);
    }
  }, [activities, activityQuestion.slug, setValue, getValues]);

  return (
    <Controller
      control={control}
      name={activityQuestion.slug}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const currentValues: Record<string, ActivityValue> =
          value && Object.keys(value).length > 0
            ? value
            : activities.reduce(
                (acc, activity) => {
                  acc[activity.id] = { daysPerWeek: 0, minutesPerDay: 0 };
                  return acc;
                },
                {} as Record<string, ActivityValue>,
              );

        const handleActivityChange = (activityId: string, activityValues: ActivityValue) => {
          const newValues = {
            ...currentValues,
            [activityId]: activityValues,
          };
          onChange(newValues);
        };

        const handleDeselectAll = () => {
          const clearedValues: Record<string, ActivityValue> = {};
          activities.forEach((activity: Activity) => {
            clearedValues[activity.id] = { daysPerWeek: 0, minutesPerDay: 0 };
          });
          onChange(clearedValues);
        };

        const hasAnyValues = Object.values(currentValues).some(
          (activityValue) => activityValue.daysPerWeek > 0 || activityValue.minutesPerDay > 0,
        );

        const activitiesWithValues = activities.map((activity: Activity) => ({
          ...activity,
          values: currentValues[activity.id] || { daysPerWeek: 0, minutesPerDay: 0 },
        }));

        return (
          <div className="questionnaire-question-content">
            <ActivityNumberStepper
              activities={activitiesWithValues}
              onChange={handleActivityChange}
              onDeselectAll={hasAnyValues ? handleDeselectAll : undefined}
            />
          </div>
        );
      }}
    />
  );
};
